/**
 * The four setup popups, opened from the rail. For a new study they fill the draft; for an existing
 * one they change it (a new message becomes a new version; promises update the study).
 */

import { ChevronDown, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { toast } from "sonner";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  useAddVariant,
  useCreateStudy,
  useDraftEstimate,
  useEstimate,
  useMe,
  useExamples,
  useStudy,
  useSubmitJob,
  useUpdateStudy,
} from "@/lib/queries";
import { cn } from "@/lib/utils";
import type { Schemas } from "@/api/client";

import { AudienceDialog } from "./AudienceDialog";
import { useDraft } from "./draft";
import type { DialogName } from "./Rail";

type Props = { open: DialogName | null; onClose: () => void };

export function SetupDialogs({ open, onClose }: Props) {
  const onOpenChange = (value: boolean) => !value && onClose();
  return (
    <>
      <Dialog open={open === "audience"} onOpenChange={onOpenChange}>
        {open === "audience" && <AudienceDialog onDone={onClose} />}
      </Dialog>
      <Dialog open={open === "message"} onOpenChange={onOpenChange}>
        {open === "message" && <MessageDialog onDone={onClose} />}
      </Dialog>
      <Dialog open={open === "promises"} onOpenChange={onOpenChange}>
        {open === "promises" && <PromisesDialog onDone={onClose} />}
      </Dialog>
      <Dialog open={open === "ask"} onOpenChange={onOpenChange}>
        {open === "ask" && <AskDialog onDone={onClose} />}
      </Dialog>
    </>
  );
}

function MessageDialog({ onDone }: { onDone: () => void }) {
  const { studyId } = useParams();
  const study = useStudy(studyId).data;
  const { draft, update } = useDraft();
  const addVariant = useAddVariant(studyId ?? "");
  const working = (study?.variants ?? []).find((v) => v.variant_id === study?.working_variant_id);
  const [name, setName] = useState(study ? "" : draft.name);
  const [text, setText] = useState(study ? (working?.text ?? "") : draft.text);
  // Promises that came from an example, held here until Save so loading one changes nothing yet.
  const [promises, setPromises] = useState<string[] | null>(null);
  const tooShort = text.trim().length < 20;

  async function save() {
    if (!study) {
      update({ name: name.trim(), text, ...(promises ? { facts: promises, factsSaved: true } : {}) });
      onDone();
      return;
    }
    await addVariant.mutateAsync({ text, label: name.trim(), parent_id: working?.variant_id ?? null });
    toast.success("Saved as a new version");
    onDone();
  }

  return (
    <DialogContent className="sm:max-w-2xl">
      <DialogHeader>
        <DialogTitle>{study ? "A new version of the message" : "The message"}</DialogTitle>
        <DialogDescription>Exactly as your audience would see it.</DialogDescription>
      </DialogHeader>
      <div className="space-y-3">
        {!study && (
          <ExamplePicker
            hasContent={Boolean(name.trim() || text.trim())}
            onLoad={(example) => {
              setName(example.study_name);
              setText(example.text);
              setPromises(example.promises);
              toast.success(
                example.promises.length
                  ? `Loaded, with ${example.promises.length} promise${example.promises.length === 1 ? "" : "s"} to keep`
                  : "Loaded",
              );
            }}
          />
        )}
        <div className="space-y-1.5">
          <Label htmlFor="message-name">{study ? "Version label" : "Study name"}</Label>
          <Input id="message-name" value={name} onChange={(e) => setName(e.target.value)}
                 placeholder={study ? "e.g. Softer headline" : "e.g. Launch ad"} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="message-text">Message</Label>
          <Textarea id="message-text" rows={10} value={text} onChange={(e) => setText(e.target.value)} />
          {tooShort && <p className="text-xs text-muted-foreground">At least 20 characters.</p>}
        </div>
      </div>
      <DialogFooter>
        <Button disabled={tooShort || addVariant.isPending} onClick={() => void save()}>
          {study ? "Save as new version" : "Save"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function PromisesDialog({ onDone }: { onDone: () => void }) {
  const { studyId } = useParams();
  const study = useStudy(studyId).data;
  const { draft, update } = useDraft();
  const updateStudy = useUpdateStudy(studyId ?? "");
  const [text, setText] = useState(((study ? study.locked_facts : draft.facts) ?? []).join("\n"));

  async function save() {
    const facts = text.split("\n").map((f) => f.trim()).filter(Boolean);
    if (study) {
      await updateStudy.mutateAsync({ locked_facts: facts });
      toast.success("Promises saved");
    } else {
      update({ facts, factsSaved: true });
    }
    onDone();
  }

  return (
    <DialogContent className="sm:max-w-xl">
      <DialogHeader>
        <DialogTitle>Promises to keep</DialogTitle>
        <DialogDescription>
          One per line. Every suggested version must keep these true - prices, terms, legal wording. Optional.
        </DialogDescription>
      </DialogHeader>
      <Textarea rows={7} aria-label="Promises, one per line" value={text} onChange={(e) => setText(e.target.value)}
                placeholder={"Minimum term 12 months.\nPrices include VAT."} />
      <DialogFooter>
        <Button disabled={updateStudy.isPending} onClick={() => void save()}>
          Save promises
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function AskDialog({ onDone }: { onDone: () => void }) {
  const { studyId } = useParams();
  const navigate = useNavigate();
  const study = useStudy(studyId).data;
  const me = useMe().data;
  const { draft, clear } = useDraft();
  const createStudy = useCreateStudy();
  const submit = useSubmitJob();
  const [live, setLive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canGoLive = Boolean(me?.live_runs_allowed);
  const mode = study ? study.mode : live ? "live" : "mock";
  // Before the study exists there is no id to price, so the draft is priced instead. Without this
  // the cost box had nothing to wait for and waited for ever.
  const saved = useEstimate(studyId, study?.working_variant_id ?? null, Boolean(study));
  const drafted = useDraftEstimate(mode, draft.audienceId ?? null, !study && mode === "live");
  const estimate = study ? saved : drafted;

  useEffect(() => setError(null), [live]);

  async function ask() {
    setError(null);
    try {
      let target = studyId;
      if (!study) {
        const created = await createStudy.mutateAsync({
          name: draft.name || "Untitled study",
          text: draft.text,
          locked_facts: draft.facts,
          mode,
          audience_id: draft.audienceId,
        });
        target = created.study_id;
        clear();
      }
      await submit.mutateAsync({ studyId: target!, variantId: study?.working_variant_id ?? null });
      toast.success("Asking the community - progress shows in the rail");
      onDone();
      navigate(`/studies/${target}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "could not start");
    }
  }

  const e = estimate.data;
  return (
    <DialogContent className="sm:max-w-lg">
      <DialogHeader>
        <DialogTitle>Ask the community</DialogTitle>
        <DialogDescription>
          The personas read the message, post, reply and follow each other; you see how opinion moves.
        </DialogDescription>
      </DialogHeader>

      {!study && (
        <div className="flex items-center justify-between rounded-lg border border-white/10 p-3">
          <div>
            <Label htmlFor="live-mode">Live run</Label>
            <p className="text-xs text-muted-foreground">
              {canGoLive ? "Real model calls, charged from your credit." : "Live runs are invite-only during early access."}
            </p>
          </div>
          <Switch id="live-mode" checked={live} disabled={!canGoLive} onCheckedChange={setLive} />
        </div>
      )}

      <div className="rounded-lg bg-black/30 p-3 text-sm">
        {mode === "mock" ? (
          <p>Mock run: synthetic reactions, <strong>free</strong>. Good for trying the flow.</p>
        ) : e ? (
          <p>
            Reserves <strong>${e.reserve_usd.toFixed(2)}</strong> of credit (likely cost ${e.likely_low_usd.toFixed(2)}–$
            {e.likely_high_usd.toFixed(2)}); unused credit returns when it finishes. Available: ${e.available_usd.toFixed(2)}.
          </p>
        ) : estimate.isError ? (
          // A price that cannot be worked out is not a price of zero. Say so, and offer another go.
          <p className="text-[#ff8f75]">
            The cost could not be worked out: {estimate.error.message}.{" "}
            <button type="button" className="underline underline-offset-2" onClick={() => void estimate.refetch()}>
              Try again
            </button>
          </p>
        ) : (
          <p className="flex items-center gap-2 text-muted-foreground">
            <Loader2 aria-hidden="true" className="size-4 animate-spin" />
            Working out the cost…
          </p>
        )}
        {mode === "live" && e?.affordable === false && (
          // The credit wall (decision 7.4): work stops, and the way out is one click.
          <p className="mt-2 text-[#ff8f75]">
            That is more than your ${e.available_usd.toFixed(2)} of credit.{" "}
            <Link to="/billing" className="underline underline-offset-2" onClick={onDone}>
              Buy more credit or change plan
            </Link>
            .
          </p>
        )}
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <DialogFooter>
        <Button variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        {/* Nothing is confirmed at a price that has not been shown: while a live estimate is still
            being worked out there is nothing on screen to agree to. */}
        <Button disabled={createStudy.isPending || submit.isPending
                          || (mode === "live" && (estimate.isPending || e?.affordable === false))}
                onClick={() => void ask()}>
          {createStudy.isPending || submit.isPending ? (
            <>
              <Loader2 aria-hidden="true" className="size-4 animate-spin" /> Starting…
            </>
          ) : (
            "Confirm and ask"
          )}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

/**
 * Something to start from, when the hardest part is a blank box.
 *
 * Offered only when starting a study: loading one over an existing version would silently replace
 * the wording a result was produced from. Each example brings its promises with it, so step three
 * is filled at the same time and the guardrails have something to guard - which is what these were
 * written to exercise.
 *
 * Loading into empty fields is one click. If there is something to lose it asks first, because
 * retyping a message you had just drafted is the kind of small loss nobody builds an undo for.
 */
function ExamplePicker({
  hasContent,
  onLoad,
}: {
  hasContent: boolean;
  onLoad: (example: Schemas["ExampleView"]) => void;
}) {
  const examples = useExamples();
  const [showing, setShowing] = useState(false);
  const [chosen, setChosen] = useState<Record<string, string>>({});
  const [confirming, setConfirming] = useState<Schemas["ExampleView"] | null>(null);
  const groups = examples.data?.categories ?? [];

  if (groups.length === 0) return null;

  function choose(example: Schemas["ExampleView"]) {
    if (hasContent) setConfirming(example);
    else onLoad(example);
  }

  return (
    <div className="rounded-lg border border-white/10">
      <button type="button" onClick={() => setShowing(!showing)}
              className="flex w-full items-center gap-1.5 px-3 py-2 text-left text-sm">
        <ChevronDown aria-hidden="true"
                     className={cn("size-4 transition-transform", showing && "rotate-180")} />
        Start from an example
      </button>

      {showing && (
        <div className="space-y-3 border-t border-white/10 p-3">
          {groups.map((group) => {
            const picked = chosen[group.name] ?? group.examples[0]?.key;
            const example = group.examples.find((one) => one.key === picked);
            return (
              <div key={group.name} className="space-y-1.5">
                <Label htmlFor={`example-${group.name}`}>{group.name}</Label>
                <p className="text-xs text-muted-foreground">{group.description}</p>
                <div className="flex gap-2">
                  <select
                    id={`example-${group.name}`}
                    className="h-9 flex-1 rounded-md border border-white/10 bg-[#0f1115] px-2 text-sm"
                    value={picked}
                    onChange={(event) => setChosen({ ...chosen, [group.name]: event.target.value })}
                  >
                    {group.examples.map((one) => (
                      <option key={one.key} value={one.key}>{one.label}</option>
                    ))}
                  </select>
                  <Button variant="outline" disabled={!example}
                          onClick={() => example && choose(example)}>
                    Load
                  </Button>
                </div>
                {example?.standing && (
                  <p className="text-xs text-muted-foreground">
                    <span className="text-foreground">Where this stands: {example.standing}.</span>{" "}
                    {example.standing_note}
                  </p>
                )}
                <p className="text-[11px] text-muted-foreground/80">{group.disclaimer}</p>
              </div>
            );
          })}

          {confirming && (
            <Alert variant="destructive">
              <AlertDescription className="space-y-2 text-xs">
                <p>Replace what you have written with “{confirming.label}”?</p>
                <div className="flex gap-2">
                  <Button size="xs" onClick={() => { onLoad(confirming); setConfirming(null); }}>
                    Replace it
                  </Button>
                  <Button size="xs" variant="ghost" onClick={() => setConfirming(null)}>
                    Keep mine
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}
        </div>
      )}
    </div>
  );
}
